// VenueSelector — the global venue segmented control (Deribit · OKX · …), the
// highest-level "what am I looking at" dimension. Twin of the TokenSelector:
// switching venue is CLIENT-SIDE re-indexing of the one nested bundle (no
// re-request), and it resets the token to the new venue's default. Populated
// from the payload's `venues[]` manifest.

import { Stack, Box } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import { venueLabel } from '../../../../providers/options-flow/options-flow.helpers';
import { VenueInfo } from '../../../../providers/options-flow/types';

interface VenueSelectorProps {
  value: string; // active venue id
  venues: VenueInfo[];
  onChange: (venue: string) => void;
}

export const VenueSelector = ({ value, venues, onChange }: VenueSelectorProps) => {
  // A single venue is not a choice — don't render a one-item control.
  if (venues.length < 2) return null;
  return (
    <Stack
      direction="row"
      role="tablist"
      aria-label="Venue"
      sx={{
        display: 'inline-flex',
        border: `1px solid ${tokens.border.default}`,
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      {venues.map((v, i) => {
        const active = v.venue === value;
        return (
          <Box
            key={v.venue}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(v.venue)}
            sx={{
              cursor: 'pointer',
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.4px',
              px: '11px',
              py: '4px',
              borderLeft: i === 0 ? 'none' : `1px solid ${tokens.border.default}`,
              color: active ? tokens.flow.accent : tokens.text.secondary,
              backgroundColor: active ? tokens.bg.widget : 'transparent',
              '&:hover': { backgroundColor: tokens.bg.widget },
            }}
          >
            {venueLabel(v)}
          </Box>
        );
      })}
    </Stack>
  );
};
